import { AVATAR_SIZES } from "@cmp-src/components/UserAvatar/UserAvatar";
import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { ResourceLocation } from "@cmp-src/data-display/resource/ResourceLocation";
import { ISchedule } from "@gettactic/api";
import { AgendaCardLayout } from "@gettactic/components/src/components/reservation-card/AgendaCardLayout";
import { createMapLink, findCheckinTargetSlot } from "@gettactic/helpers";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { ResourceTypes } from "@gettactic/helpers/src/resources/utils";
import { getScheduleAttendeeSlots } from "@gettactic/helpers/src/schedules/getScheduleAttendees";
import { useAreas, useAuthenticated } from "@gettactic/hooks";
import { format } from "date-fns";
import { useRouter } from "next/navigation";

interface ScheduleInfoCardProps {
  schedule: ISchedule & { office?: { time_zone?: string } | null };
  className?: string;
  innerClassName?: string;
  onClick?: () => void;
}

export function ScheduleInfoCard({
  schedule,
  className,
  innerClassName,
  onClick
}: ScheduleInfoCardProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { areas: areaList } = useAreas(schedule?.resource?.office_id ?? null);
  const router = useRouter();

  if (!schedule?.resource?.office_id) {
    return null;
  }

  // /schedules stamps times with a literal "Z" that doesn't mean UTC — it's
  // a wall clock already in the office's zone (schedule.time_zone), unlike
  // the agenda endpoint's real UTC. Displaying it in that same office zone
  // is therefore the identity transform: strip the fake "Z" and read the
  // digits as-is, rather than running them through a real tz conversion
  // (which would double-apply the office's UTC offset).
  const formatTime = (iso: string) =>
    format(new Date(dateStringToLocalTime(iso)), "HH:mm");
  const formatDate = (iso: string) =>
    format(new Date(dateStringToLocalTime(iso)), "EEE, MMM d");

  // One entry per attendee: a reservation holding several rooms repeats every
  // attendee once per room.
  const participants = getScheduleAttendeeSlots(schedule).map((slot) => ({
    id: slot.user.id,
    name: slot.user.name,
    email: slot.user.email,
    profile_photo_url: slot.user.profile_photo_url,
    roles: slot.user.roles || []
  }));

  const office = authenticatedUser.offices?.byId[schedule.resource.office_id];
  const officeName = office?.name || "Unknown Office";

  const getResourceTypeName = (type: string) => {
    switch (type) {
      case ResourceTypes.desk:
        return "Desk";
      case ResourceTypes.workspace:
        return "Workspace";
      case ResourceTypes.meeting_room:
        return "Meeting Room";
      case ResourceTypes.parking_space:
        return "Parking Space";
      case ResourceTypes.parking_lot:
        return "Parking Lot";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const targetSlot = findCheckinTargetSlot(
    schedule,
    authenticatedUser.user?.id ?? ""
  );

  const location =
    !!targetSlot && schedule.resource.parent_id ? (
      <ResourceLocation
        location={officeName}
        floor={areaList?.byId[schedule.resource.parent_id]?.name}
        area={areaList?.byId[schedule.resource.id]?.name}
      />
    ) : null;

  const isMeetingRoom = schedule.resource.type === "meeting_room";

  const handleClick =
    onClick ??
    (() => {
      // Same de-fake-Z'd wall clock as the card's own date/time above, and
      // startTime/endTime (not just date) so the map opens scrolled to the
      // reservation's actual time instead of just the right day.
      const mapLink = createMapLink({
        date: dateStringToLocalTime(schedule.start),
        officeId: schedule.resource.office_id,
        resourceId: schedule.resource.id,
        floorId: schedule.resource.parent_id || undefined,
        startTime: dateStringToLocalTime(schedule.start),
        endTime: schedule.end ? dateStringToLocalTime(schedule.end) : undefined
      });
      router.push(mapLink);
    });

  return (
    <AgendaCardLayout
      className={className}
      innerClassName={innerClassName}
      onCardClick={handleClick}
      topLeft={
        <div className="text-neutral-900">
          <span className="font-semibold">{formatDate(schedule.start)}</span>
          <span className="text-neutral-700">
            {" "}
            • {formatTime(schedule.start)} - {formatTime(schedule.end)}
          </span>
          <div>
            {getResourceTypeName(schedule.resource.type)}{" "}
            {schedule.resource.name}
          </div>
        </div>
      }
      bottomLeft={
        <div className="flex flex-col space-y-2">
          {isMeetingRoom && (
            <div className="flex flex-col space-y-1 cursor-pointer">
              {location}
            </div>
          )}
        </div>
      }
      bottomRight={
        isMeetingRoom ? (
          <div className="flex flex-col space-y-1">
            <UserAvatarStack
              users={participants}
              avatarSize={AVATAR_SIZES.ROUNDED_SM}
            />
          </div>
        ) : (
          !!location && (
            <div className="flex w-full flex-col justify-start space-y-1">
              {location}
            </div>
          )
        )
      }
    />
  );
}
